import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { TrashIcon } from '@phosphor-icons/react';
import type { TaskResponse } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { Button } from 'src/components/ui/button';
import { toast } from 'src/components/ui/toast';

/**
 * Confirm-then-soft-delete for one task. The task leaves every list and agent
 * queue but still opens from links, read-only. Callers own `open` so the
 * dialog can outlive the menu that opened it.
 */
export function DeleteTaskDialog({
  taskId,
  open,
  onOpenChange,
  onDeleted,
}: {
  taskId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => rpcClient.tasks.delete({ id: taskId }),
    onSuccess: () => {
      invalidateTaskQueries(queryClient);
      toast.success(
        gt('Task deleted', { context: 'toast after deleting a task' }),
      );
      onOpenChange(false);
      onDeleted?.();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not delete the task', {
              context: 'toast after deleting a task failed',
            }),
      );
    },
  });

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!remove.isPending) onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="confirm dialog title: delete a task">
              Delete this task?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="confirm dialog body: what deleting a task does">
              The task is removed from every list and no agent will pick it up.
              Links to it still open it, read-only.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            <T context="dialog cancel button">Cancel</T>
          </AlertDialogCancel>
          {/* A plain button, not AlertDialogAction: the dialog stays open
              until the delete settles, and success closes it. */}
          <Button
            type="button"
            variant="destructive"
            data-testid="confirm-delete-task"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            <TrashIcon className="size-4" />
            <T context="confirm button: delete the task">Delete Task</T>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Banner on a soft-deleted task opened from a reference. */
export function TaskDeletedBanner({ task }: { task: TaskResponse }) {
  if (!task.deletedAt) return null;
  return (
    <div className="mb-2 flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-xs text-red-950">
      <TrashIcon className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <T context="task page banner: this task was deleted and is read-only">
          This task was deleted. It no longer appears in lists or agent queues
          and cannot be changed.
        </T>
      </span>
    </div>
  );
}
